import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { getServices } from '@infrastructure/services';
import { Modal, Spinner } from '../design-system';
import styles from './DocumentViewer.module.css';

/**
 * El visor de documentos del portal: uno solo para los tres sitios que guardan
 * archivos.
 *
 * **Por qué dejó de ser una pestaña nueva.** El expediente abría cada documento
 * con `window.open`, que en el mejor caso saca a quien lo revisa del portal y en
 * el peor se lo come el bloqueador de ventanas emergentes —el clic llega
 * *después* de una llamada a Storage, así que el navegador ya no lo reconoce
 * como un gesto del usuario—. Ver ocho papeles de una persona eran ocho
 * pestañas y ninguna dice de quién es lo que estás mirando.
 *
 * **Y por qué vive aquí y no dentro de una pantalla.** Nació en Aprobaciones,
 * para el registro firmado, y es lo que este componente extrae: la misma caja
 * sirve para el expediente y para el certificado del IMSS, que hasta hoy tenía
 * un botón que sólo enseñaba un aviso.
 *
 * **Las URL se piden al abrir, nunca al pintar la lista.** Son URL con token y
 * resolverlas por adelantado sería una llamada a Storage por documento cada vez
 * que la pantalla se refresca, para enlaces que casi nadie abre. Una vez
 * resuelta se guarda mientras el visor siga abierto, así que volver a un
 * documento ya visto es instantáneo.
 *
 * **Un `iframe`, no `<object>`**: el CSP de producción lleva `object-src 'none'`
 * y el visor quedaría en blanco justo donde no se puede probar en local. Por lo
 * mismo `firebasestorage.googleapis.com` está en `frame-src` de `firebase.json`;
 * sin esa línea esto funciona en desarrollo y falla desplegado.
 */

export interface DocumentoVisible {
  /** Lo que se lee arriba: «Acta de nacimiento», «Registro de VC-0059». */
  readonly titulo: string;
  /** La ruta en Storage. La URL se resuelve al abrir. */
  readonly path: string;
  /** El nombre con el que se guarda al descargar. */
  readonly nombreArchivo: string;
}

interface Props {
  readonly documentos: readonly DocumentoVisible[];
  /** Cuál se abre primero. El resto queda a un clic, sin cerrar el visor. */
  readonly inicial?: number;
  /** De quién son estos papeles. Va bajo el título, porque el título ya dice qué es. */
  readonly subtitulo?: string;
  readonly onClose: () => void;
}

type Estado = 'cargando' | 'listo' | 'falla';

export const DocumentViewer = ({ documentos, inicial = 0, subtitulo, onClose }: Props): ReactNode => {
  const [indice, setIndice] = useState(Math.min(Math.max(inicial, 0), Math.max(documentos.length - 1, 0)));
  const [estado, setEstado] = useState<Estado>('cargando');
  const [url, setUrl] = useState<string | null>(null);
  const [intento, setIntento] = useState(0);
  /* Las URL ya resueltas, mientras el visor siga abierto: pasar de un documento
     al siguiente y volver no debe pedir dos veces lo mismo. */
  const cache = useRef(new Map<string, string>());

  const actual = documentos[indice];
  const path = actual?.path ?? null;

  useEffect(() => {
    if (path === null) return;

    const guardada = cache.current.get(path);
    if (guardada !== undefined) {
      setUrl(guardada);
      setEstado('listo');
      return;
    }

    let vivo = true;
    setEstado('cargando');
    setUrl(null);
    void getServices()
      .documents.resolveDownloadUrl(path)
      .then((resultado) => {
        if (!vivo) return;
        if (resultado.ok) {
          cache.current.set(path, resultado.value);
          setUrl(resultado.value);
          setEstado('listo');
        } else {
          setEstado('falla');
        }
      });
    return () => {
      vivo = false;
    };
  }, [path, intento]);

  const mover = useCallback(
    (paso: number) => {
      setIndice((previo) => Math.min(Math.max(previo + paso, 0), documentos.length - 1));
    },
    [documentos.length],
  );

  /* Las flechas pasan de un documento a otro, como en cualquier visor. Escape lo
     cierra, y de eso ya se encarga el modal. */
  useEffect(() => {
    if (documentos.length < 2) return;
    const alPulsar = (evento: KeyboardEvent): void => {
      if (evento.key === 'ArrowRight') mover(1);
      if (evento.key === 'ArrowLeft') mover(-1);
    };
    document.addEventListener('keydown', alPulsar);
    return () => {
      document.removeEventListener('keydown', alPulsar);
    };
  }, [documentos.length, mover]);

  if (actual === undefined) return null;

  const hayVarios = documentos.length > 1;

  return (
    <Modal
      /* El nombre de quien lo firma va en el título y no en una línea aparte:
         era la única otra cosa que justificaba una segunda fila. */
      title={subtitulo === undefined ? actual.titulo : `${actual.titulo} · ${subtitulo}`}
      onClose={onClose}
      size="full"
      frame="viewer"
      headerActions={
        <>
          {hayVarios && (
            <>
              <button
                type="button"
                className={styles.flecha}
                onClick={() => {
                  mover(-1);
                }}
                disabled={indice === 0}
                aria-label="Documento anterior"
              >
                ‹
              </button>
              <span className={styles.contador}>
                {indice + 1} de {documentos.length}
              </span>
              <button
                type="button"
                className={styles.flecha}
                onClick={() => {
                  mover(1);
                }}
                disabled={indice === documentos.length - 1}
                aria-label="Documento siguiente"
              >
                ›
              </button>
            </>
          )}
          <button
            type="button"
            className={styles.accion}
            disabled={url === null}
            aria-label="Descargar"
            onClick={() => {
              if (url !== null) void descargarDocumento(url, actual.nombreArchivo);
            }}
          >
            <span aria-hidden="true">↓</span>
            <span className={styles.etiqueta}>Descargar</span>
          </button>
          <button
            type="button"
            className={styles.accion}
            disabled={url === null}
            aria-label="Abrir en pestaña"
            onClick={() => {
              if (url !== null) window.open(url, '_blank', 'noopener,noreferrer');
            }}
          >
            <span aria-hidden="true">↗</span>
            <span className={styles.etiqueta}>Abrir en pestaña</span>
          </button>
        </>
      }
    >
      <div className={styles.hoja}>
        {estado === 'falla' ? (
          <div className={styles.falla}>
            <p className={styles.fallaTexto}>
              No pudimos abrir el archivo. Puede que se haya movido en Storage o que tu sesión
              ya no tenga permiso para leerlo.
            </p>
            <button
              type="button"
              className={styles.accion}
              onClick={() => {
                setIntento((n) => n + 1);
              }}
            >
              Reintentar
            </button>
          </div>
        ) : url === null ? (
          <Spinner label="Abriendo el documento" />
        ) : (
          <iframe className={styles.marco} src={url} title={actual.titulo} />
        )}
      </div>

      {/* Safari en iPhone no pinta un PDF dentro de un `iframe`: en vez de dejar
          un rectángulo vacío sin explicación, la salida está escrita. */}
      <p className={styles.pie}>¿No se ve el documento? Ábrelo en una pestaña o descárgalo.</p>
    </Modal>
  );
};

/**
 * Descargar de verdad, con el nombre del documento.
 *
 * `<a download>` se ignora cuando el archivo está en otro origen —y el de
 * Storage lo está—, así que el atributo por sí solo abre una pestaña. Traer los
 * bytes y guardarlos desde un `blob:` sí respeta el nombre, y eso exige que el
 * bucket acepte el origen del portal. **Lo acepta**: medido el 22 sep 2026
 * contra un acta de producción, la respuesta trae
 * `access-control-allow-origin: https://knit-1d484.web.app`. Lo pone
 * `npm run storage:cors`, y el comentario que decía lo contrario llevaba
 * desde antes de que se corriera.
 *
 * La segunda ruta se queda igual: un canal de vista previa o un origen nuevo no
 * están en esa lista, y ahí abrir la pestaña es mejor que un botón que no hace
 * nada.
 */
export const descargarDocumento = async (url: string, nombreArchivo: string): Promise<void> => {
  try {
    const respuesta = await fetch(url);
    if (!respuesta.ok) throw new Error(String(respuesta.status));
    const blob = await respuesta.blob();
    const objectUrl = URL.createObjectURL(blob);
    const ancla = document.createElement('a');
    ancla.href = objectUrl;
    ancla.download = nombreArchivo;
    ancla.click();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};
